import { useCallback, useEffect, useRef, useState } from "react";
import {
	delay,
	deleteAllFilesAndFolders,
	fillCommandLineParams,
	killAllSubProcess,
	waitForSubProcess
} from "@features/FxLauncherLoader/utils/helpers";
import styles from "@features/StepsContent/components/StepStart/stepStart.module.scss";
import { Button, Center, Image, Loader, Modal, Stack, Text, Title } from "@mantine/core";

import { useTranslation } from "react-i18next";
import { useDisclosure } from "@mantine/hooks";
import { fxLauncherLoaderStore } from "@features/FxLauncherLoader/store/fxLauncherLoaderStore";
import { folderStepStore } from "@/entities/StepFolder/store/folderStepStore";
import { appCodeEnum, appStore } from "@/app/store/appStore";
import OpenApiClient, { isPreinstall } from "@/api/OpenApiClient";
import { StepStartProps } from "@features/FxLauncherLoader/utils/interfaces";
import { persistStore } from "@entities/StepFolder/store/persistStore";
import { basePath } from "@/app/AppRoutes";
import { StepNavigationButtons } from "@entities/StepNavigationButtons/StepNavigationButtons";
import { MenuBurger } from "@entities/layout/ui/NavigationLeft/MenuBurger";

export const StepStart: React.FC<StepStartProps> = (StepStartProps) => {
	const gameConfig = appStore((state) => state.gameConfig);
	const appCode = appStore((state) => state.appCode);
	const torrentId = fxLauncherLoaderStore((state) => state.torrentId);
	const autoStart = fxLauncherLoaderStore((state) => state.autoStart);

	const appProfile = folderStepStore((state) => state.appProfile);
	const installPath = persistStore((state) => state.installPath);
	const { t } = useTranslation();

	const [opened, { open, close }] = useDisclosure(false);
	const [confirmOpened, { open: confirmOpen, close: confirmClose }] = useDisclosure(false);

	const [processName, setProcessName] = useState("");

	const getCommand = useCallback(async (): Promise<string> => {
		let launchFile = appProfile?.LauncherConfiguration?.LaunchFile;
		if (launchFile) {
			launchFile = launchFile.replace(/\//g, "\\").toLowerCase();
			return `${installPath}\\${launchFile}`;
		}
		return "";
	}, [appProfile?.LauncherConfiguration?.LaunchFile, installPath]);

	const stopCommand = useCallback(async (): Promise<boolean> => {
		confirmClose();
		setProcessName(t("StartStep.Closing", "Closing"));
		const res = await stopCommandInternal();
		close();
		return res;
	}, [close, confirmClose, t]);

	async function stopCommandInternal(): Promise<boolean> {
		return await killAllSubProcess();
	}

	const  PrepareParams = useCallback(  ():string => {
		let param = gameConfig.PublicWebClientConfig.Launcher?.LaunchParams || "";
		param = fillCommandLineParams(param);
		return param;
	},[gameConfig.PublicWebClientConfig.Launcher?.LaunchParams]);

	const  installPreRequirements = useCallback(async ()=> {
		const installerFileFromConfig = appProfile?.LauncherConfiguration?.InstallerFile;

		if (installerFileFromConfig) {
			console.log(`installerFileFromConfig ${installerFileFromConfig}`);
			if (installerFileFromConfig === "bin64/VC_redist.x64.exe") {
				return;
			}

			const installCmd = `${installPath}\\${installerFileFromConfig}`.replace(/\//g, "\\").toLowerCase();
			if (installCmd) {
				const res = await FxApi.shellExecute(installCmd, "");
				if (!res.success) {
					console.log(`failed to install ${installCmd}`);
				} else {
					delay(1000);
					await waitForSubProcess();
				}
			}
		}
		// TODO: Вынести список папок которые нужно подчистить в конфиг
		if (appStore.getState().appCode === appCodeEnum.aw && isPreinstall()) {
			try {
				await deleteAllFilesAndFolders(["APP", "Armored Warfare"]);
			} catch (error) {
				console.log(`Error deleting folders: ${error}`);
			}
			try {
				await deleteAllFilesAndFolders(["APP", "13_2000009"]);
			} catch (error) {
				console.log(`Error deleting folders: ${error}`);
			}
			try {
				await deleteAllFilesAndFolders(["APP", "13_2002999"]);
			} catch (error) {
				console.log(`Error deleting folders: ${error}`);
			}
		}
	},[appProfile?.LauncherConfiguration?.InstallerFile, installPath]);

	const startGameInternal = useCallback(async () => {
		await installPreRequirements();

		const cmd = await getCommand();
		await stopCommandInternal();
		if (cmd === "") {
			throw new Error("no launch file");
		}
		const param = PrepareParams();
		await FxApi.shellExecute(cmd, param);
		setProcessName(t("StartStep.Running"));

		await delay(1000);
		if (torrentId > 0) {
			try {
				const res = await FxApi.Torrent.pauseTorrent(torrentId);
				console.log(`Torrent paused: ${JSON.stringify(res)}`);
			} catch (error) {
				console.log(`Torrent resumeTorrent failed: ${JSON.stringify(error)}`);
			}
		}
		await waitForSubProcess();

		if (torrentId > 0) {
			try {
				const res = await FxApi.Torrent.resumeTorrent(torrentId);
				console.log(`Torrent resumeTorrent: ${JSON.stringify(res)}`);
			} catch (error) {
				console.log(`Torrent resumeTorrent failed: ${JSON.stringify(error)}`);
			}
		}
		close();
	}, [PrepareParams, close, getCommand, installPreRequirements, t, torrentId]);

	const sendOnStartHook = useCallback(async () => {
		const OnStartHookUrl = gameConfig.PublicWebClientConfig.Launcher?.Links?.OnStartHookUrl;
		console.log(`OnStartHookUrl: ${OnStartHookUrl}`);
		if (OnStartHookUrl) {
			const linkFilled = fillCommandLineParams(OnStartHookUrl);
			try {
				await Promise.race([
					fetch(linkFilled, {
						mode: "no-cors",
						method: "HEAD"
					}),
					delay(5000) // 5 second timeout
				]);

				console.log(`Ping successful: ${linkFilled}`);
			} catch (error) {
				console.log(`Ping failed: ${linkFilled}`, error);
			}
		}
	}, [gameConfig.PublicWebClientConfig.Launcher?.Links?.OnStartHookUrl]);

	const startGame = useCallback(async () => {
		if (opened) {
			return;
		}
		open();
		try {
			fxLauncherLoaderStore.getState().setAutoStart(false);
			await sendOnStartHook();
			await startGameInternal();
		} catch (e) {
			console.log("Got exception in startGameInternal: ", e);
		}
	}, [open, opened, sendOnStartHook, startGameInternal]);

	const INTERVAL_DELAY = 20000; // 20 seconds
	const MAX_TRIES = 20;

	const intervalRef = useRef<NodeJS.Timeout | null>(null);
	const tryCountRef = useRef<number>(0);

	const clearIntervalIfExists = useCallback(() => {
		if (intervalRef.current) {
			clearInterval(intervalRef.current);
			intervalRef.current = null;
		}
	}, []);

	const startRequest = useCallback(async () => {
		if (!opened || tryCountRef.current >= MAX_TRIES) {
			clearIntervalIfExists();
			return;
		}

		const request = {
			GameSystemName: appStore.getState().appCode
		};

		const resp = await OpenApiClient.Profile.fxIdWebFeaturesProfileFetchFxIdGameConnectionEndpoint(request);
		if (resp.Success) {
			console.log("fetchGameConnectionEndpoint: ", resp);
			if (resp.Result?.GameSystemName === appStore.getState().appCode) {
				clearIntervalIfExists();
				return;
			}
		}

		tryCountRef.current += 1;
	}, [opened, clearIntervalIfExists]);

	useEffect(() => {
		if (opened) {
			tryCountRef.current = 0; // Reset try count when opened becomes true
			clearIntervalIfExists(); // Clear any existing interval
			intervalRef.current = setInterval(startRequest, INTERVAL_DELAY);
		} else {
			clearIntervalIfExists();
		}

		return clearIntervalIfExists;
	}, [opened, startRequest, clearIntervalIfExists]);

	useEffect(() => {
		if (autoStart && !StepStartProps.disabled) startGame();
	}, [autoStart, startGame, StepStartProps.disabled]);

	return (
		<>
			<Modal
				radius={5}
				padding={0}
				opened={opened}
				onClose={() => {}}
				closeOnEscape={false}
				withCloseButton={false}
				centered
			>
				<div id={"startPageWrapper"}>
					<MenuBurger />
					<Center>
						<Stack m={"xl"} justify="center" align="center">
							<Image
								id={`${appCode}_email`}
								h={100}
								w={100}
								src={`${basePath}${appCode}/logo-icon.svg`}
								alt="email"
							/>{" "}
							<Title>{processName}</Title>
							<Loader size={30} />
							{!confirmOpened && (
								<Button variant="light" onClick={confirmOpen}>
									{t("stepStart.stopGame")}
								</Button>
							)}
							{confirmOpened && (
								<>
									<Text>
										{t("stepStart.stopGameQuestion", "Are sure you want to close the app?")}
									</Text>
									<StepNavigationButtons
										backText={t("common.Cancel", "Cancel")}
										backOnClick={confirmClose}
										backIsDisabled={false}
										nextText={t("common.Close", "Close")}
										nextOnClick={stopCommand}
										nextIsDisabled={false}
									></StepNavigationButtons>
								</>
							)}
						</Stack>
					</Center>
				</div>
			</Modal>

			<Button
				className={styles.startButton}
				disabled={StepStartProps.disabled}
				onClick={startGame}
				w={209}
				h={48}
				radius={0}
				color="#FFCC00"
				styles={{
					root: {
						border: "none",
						outline: "none"
					},
					label: {
						fontFamily: " TT Squares",
						fontSize: 16,
						fontWeight: 700,
						//line-height: 18.4px,
						textAlign: "center",
						textTransform: "uppercase",
						color: "black"
					}
				}}
			>
				{t("common.launch")}
			</Button>
		</>
	);
};
